import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Clapperboard, Film, Loader2, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button, Card, EmptyState, ErrorState, Skeleton } from "@/components/ui";
import { useFormat } from "@/i18n/format";

/**
 * Studio home: your draft edits.
 *
 * Drafts are private to their author — publishing is how work reaches the
 * workspace — so this list is "yours", not "everyone's", and it says so.
 */
export function Studio() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation(["studio", "common"]);
  const f = useFormat();

  const projects = useQuery({
    queryKey: ["studio-projects"],
    queryFn: () => api.listStudioProjects(),
  });

  const create = useMutation({
    mutationFn: () => api.createStudioProject({}),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: ["studio-projects"] });
      void navigate({ to: "/studio/$projectId", params: { projectId: project.id } });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteStudioProject(id),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["studio-projects"] }),
  });

  if (projects.isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (projects.isError) {
    return (
      <ErrorState title={t("home.loadError")} error={projects.error} />
    );
  }

  const list = projects.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-text">Studio</h1>
          <p className="mt-0.5 text-[13px] text-text-muted">
            {t("home.intro")}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Plus size={14} />
          )}
          {t("home.newProject")}
        </Button>
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={<Clapperboard size={20} />}
          title={t("home.emptyTitle")}
          description={t("home.emptyDescription")}
        />
      ) : (
        <div className="space-y-2">
          {list.map((project) => (
            <Card key={project.id} className="p-0">
              <div className="flex items-center justify-between gap-3 p-4">
                <Link
                  to="/studio/$projectId"
                  params={{ projectId: project.id }}
                  className="min-w-0 flex-1"
                >
                  <h3 dir="auto" className="truncate text-[15px] font-semibold text-text">
                    {project.title}
                  </h3>
                  <p className="mt-1 flex items-center gap-3 text-[12.5px] text-text-muted">
                    <span className="inline-flex items-center gap-1.5">
                      <Film size={12} />
                      {t("home.clipCount", { count: project.clipCount })}
                    </span>
                    {project.durationMs > 0 ? (
                      <span className="tabular-nums">
                        {f.duration(project.durationMs)}
                      </span>
                    ) : null}
                    <span>
                      {t("home.edited", { date: f.dateTime(project.updatedAt) })}
                    </span>
                  </p>
                </Link>
                <div className="flex shrink-0 items-center gap-2">
                  {project.publishedRecordingId ? (
                    <Link
                      to="/r/$recordingId"
                      params={{ recordingId: project.publishedRecordingId }}
                      className="text-[12.5px] text-accent hover:underline"
                    >
                      {t("home.lastPublish")}
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    aria-label={t("home.deleteProject", { title: project.title })}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(project.id)}
                    className="rounded p-1.5 text-text-faint hover:bg-surface-hover hover:text-danger"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
